import { useEntity } from "@components/entity/useEntity";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EntityProfileFormFields } from "@components/onboarding/components/EntityProfileFormFields";
import {
    entityProfileFormSchema,
    EntityProfileFormSchema,
} from "@components/onboarding/useEntityProfileFormConfig";
import {
    EntityProfileRevenueBracket,
    EntityProfileType,
} from "@common/types/entity";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useEditEntityMutation } from "@api/entity.api";
import { DEFAULT_COUNTRY_CODE } from "@components/onboarding/constants";
import { Form, FormikProvider, useFormik } from "formik";
import { FC, ReactNode, useState } from "react";
import { EditModalFooter } from "./EditModalFooter";

interface Props {
    header?: ReactNode;
    show: boolean;
    onClose?(): void;
}

export const UpdateEntityProfileModal: FC<Props> = ({
    header,
    show,
    onClose,
}) => {
    const entity = useEntity();
    const [loading, setLoading] = useState(false);
    const { error, toast } = useToaster();
    const editEntity = useEditEntityMutation(entity);

    const formik = useFormik<EntityProfileFormSchema>({
        initialValues: {
            name: entity.name ?? "",
            legalType: entity.legalType ?? EntityProfileType.NO_LEGAL_ENTITY,
            industries: entity.profile?.industries ?? [],
            industryOther: entity.profile?.industryOther,
            annualRevenue:
                entity.profile?.annualRevenue ??
                EntityProfileRevenueBracket.LESS_THAN_50K,
            country: entity.profile?.country ?? DEFAULT_COUNTRY_CODE,
        },
        validateOnMount: true,
        validate: validateFormikWithZodSchema(entityProfileFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({ legalType, name, ...profile }) => {
                try {
                    await editEntity.mutateAsync({
                        name,
                        legalType,
                        profile: {
                            ...entity.profile,
                            ...profile,
                        },
                    });

                    toast("Profile updated");
                    onClose?.();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    });

    const finalHeader = header ?? `Update ${entity.name} profile`;

    return (
        <PlainModal show={show} onHide={onClose} hideCloseButton={!onClose}>
            <StandardModalHeader>{finalHeader}</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <EntityProfileFormFields
                        hidePhoneNumberInput
                        isOnboardingFlow={false}
                    />
                    <EditModalFooter
                        isValid={formik.isValid}
                        isSubmitting={formik.isSubmitting}
                        onClose={onClose}
                    />
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
